import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function TipButton({ creator, className = "" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [amounts, setAmounts] = useState([]);
  const [pick, setPick] = useState("tip_500");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) api.get("/tip-amounts").then((r) => setAmounts(r.data)); }, [open]);

  if (!creator?.is_creator || user?.id === creator.id) return null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/tip", { creator_id: creator.id, lookup_key: pick, message, origin_url: window.location.origin });
      localStorage.setItem("velvet_pending_creator", creator.username);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => (user ? setOpen(true) : nav("/login"))} data-testid="tip-btn"
        className={`flex items-center justify-center gap-2 rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10 ${className}`}>
        <Gift className="h-4 w-4" strokeWidth={1.75} /> Tip
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-md border-[#2A2E37] bg-[#121418] text-white" data-testid="tip-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-tight">Tip @{creator.username}</DialogTitle>
            <DialogDescription className="text-xs text-gray-400">A one-time thank-you. Your note is delivered to their inbox.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2" data-testid="tip-amounts">
            {amounts.map((a) => (
              <button key={a.lookup_key} onClick={() => setPick(a.lookup_key)} data-testid={`tip-amount-${a.lookup_key}`}
                className={`rounded-sm border py-2.5 text-sm font-semibold tabular-nums transition-colors ${
                  pick === a.lookup_key ? "border-white bg-white text-[#090A0C]" : "border-[#2A2E37] bg-white/5 hover:border-white/30"}`}>
                ${a.amount}
              </button>
            ))}
          </div>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} placeholder="Add a note (optional)"
            data-testid="tip-message-input"
            className="min-h-[80px] w-full rounded-sm border border-[#2A2E37] bg-white/5 px-3 py-2.5 text-sm outline-none focus:border-white/40" />
          <button onClick={send} disabled={busy} data-testid="tip-submit-btn"
            className="btn-neon flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold text-white disabled:opacity-70">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send ${amounts.find((a) => a.lookup_key === pick)?.amount ?? ""} tip
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
